import { useEffect, useRef, useState } from "react";

// ----------------------------------------------------------------------

// Preferred recording formats, most compatible first. The extension is used for the uploaded
// file name so the backend can validate it against its allowed audio extensions.
const RECORDING_FORMATS = [
  { mimeType: "audio/webm", extension: "webm" },
  { mimeType: "audio/mp4", extension: "m4a" }, // the only one iOS Safari supports
  { mimeType: "audio/ogg", extension: "ogg" },
];

// 64 kbps mono is plenty for speech recognition and keeps the payload far below the endpoint's 5 MB
// limit: 60 s is roughly 480 KB. Browsers that ignore the hint encode at 128-256 kbps, which is still
// under 2 MB for the same 60 s.
const AUDIO_BITS_PER_SECOND = 64000;

export const SILENCE_TIMEOUT_MS = 5000; // silence, not total duration
export const NO_SPEECH_TIMEOUT_MS = 8000; // nothing was ever heard
export const GRACE_MS = 1200; // the silence timer is not armed before this
export const MAX_DURATION_MS = 60000; // hard safety cap (see AUDIO_BITS_PER_SECOND)
export const MAX_BYTES = 4.5 * 1024 * 1024;

// RMS bounds for the adaptive silence threshold. Digital silence sits around 0.000-0.003, a quiet room
// through a laptop mic around 0.005-0.02, and speech peaks at 0.05-0.3.
const MIN_THRESHOLD = 0.02;
const MAX_THRESHOLD = 0.08;

export const STOP_REASON = {
  manual: "manual", // the user pressed stop/send
  silence: "silence", // auto: the user spoke, then went quiet
  noSpeech: "noSpeech", // auto: nothing ever crossed the threshold, so nothing is uploaded
  maxDuration: "maxDuration",
  cancelled: "cancelled", // discarded on purpose (or unmounted): no blob is delivered
};

export const RECORDER_ERROR = {
  unsupported: "unsupported",
  permission: "permission",
  notFound: "notFound",
  empty: "empty",
  tooLarge: "tooLarge",
};

function getSupportedFormat() {
  if (typeof MediaRecorder === "undefined") return null;

  return RECORDING_FORMATS.find(format => MediaRecorder.isTypeSupported(format.mimeType)) ?? null;
}

// Only one microphone may be live in the whole app: a form can render many speech-enabled fields, and
// two simultaneous recordings would fight over the device and over the level meter. The owner is the
// hook instance's stable token rather than its stop function, whose identity changes on every render.
let activeRecording = null; // { owner, stop }

// ----------------------------------------------------------------------

/**
 * Owns the recording lifecycle: format selection, the MediaRecorder, live level metering and the
 * silence auto-stop, plus the teardown of every resource involved.
 *
 * The measured level is exposed as a ref rather than state on purpose: it updates ~60 times a second,
 * and putting it in state would re-render the field (and its react-hook-form Controller) every frame.
 * Consumers read `levelRef.current` from inside their own animation frame and write the DOM directly.
 */
export function useAudioRecorder({
  onComplete, // ({ blob, fileName, durationMs, stopReason }) => void
  onError, // (RECORDER_ERROR, stopReason?) => void
  silenceTimeout = SILENCE_TIMEOUT_MS,
  noSpeechTimeout = NO_SPEECH_TIMEOUT_MS,
  graceMs = GRACE_MS,
  maxDurationMs = MAX_DURATION_MS,
  maxBytes = MAX_BYTES,
} = {}) {
  const [isStarting, setIsStarting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [startedAt, setStartedAt] = useState(null); // only feeds the elapsed clock

  // Callbacks live in refs so the MediaRecorder and animation-frame callbacks never go stale and never
  // need rebinding mid-recording.
  const onCompleteRef = useRef(onComplete);
  const onErrorRef = useRef(onError);
  useEffect(() => {
    onCompleteRef.current = onComplete;
    onErrorRef.current = onError;
  });

  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const formatRef = useRef(null);
  const stopReasonRef = useRef(STOP_REASON.manual);
  // A start() is in flight, and the reason of a stop() requested while it still was. Both are refs
  // rather than state because stop() is reachable from callbacks holding an older render's closure.
  const startingRef = useRef(false);
  const pendingStopRef = useRef(null);

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const bufferRef = useRef(null);
  const rafRef = useRef(null);
  const maxTimerRef = useRef(null);

  const levelRef = useRef(0); // smoothed RMS 0..1, read by the meters. Never state.
  const floorRef = useRef(0); // running noise-floor estimate
  const hasSpeechRef = useRef(false);
  const lastVoiceAtRef = useRef(0);
  const startedAtRef = useRef(0);
  const mountedRef = useRef(true);
  // Stable identity for this hook instance, used to tell whether the app-wide active recording
  // is still ours before clearing it.
  const instanceRef = useRef({});

  const releaseStream = () => {
    // Stopping the tracks is what turns the operating system's recording indicator off.
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  };

  const stopMetering = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;

    // Browsers cap the number of live AudioContexts (around six in Chrome), so leaking one per
    // recording would break the feature after a handful of uses.
    sourceNodeRef.current?.disconnect();
    analyserRef.current?.disconnect();
    audioContextRef.current?.close().catch(() => {});
    sourceNodeRef.current = null;
    analyserRef.current = null;
    audioContextRef.current = null;
    bufferRef.current = null;
    levelRef.current = 0;
  };

  const clearTimers = () => {
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    maxTimerRef.current = null;
  };

  const handleRecorderStop = () => {
    const { mimeType, extension } = formatRef.current ?? {};
    const blob = new Blob(chunksRef.current, { type: mimeType });
    const durationMs = performance.now() - startedAtRef.current;
    const reason = stopReasonRef.current;

    chunksRef.current = [];
    releaseStream();
    mediaRecorderRef.current = null;
    if (activeRecording?.owner === instanceRef.current) activeRecording = null;

    if (!mountedRef.current || reason === STOP_REASON.cancelled) return;

    if (reason === STOP_REASON.noSpeech || !blob.size) {
      onErrorRef.current?.(RECORDER_ERROR.empty, reason);
      return;
    }

    // A payload over the endpoint's limit comes back as 413, which the axios interceptor does not turn
    // into a message, so the user would see nothing at all. Catch it here instead.
    if (blob.size > maxBytes) {
      onErrorRef.current?.(RECORDER_ERROR.tooLarge, reason);
      return;
    }

    onCompleteRef.current?.({
      blob,
      fileName: `recording.${extension}`,
      durationMs,
      stopReason: reason,
    });
  };

  const stop = (reason = STOP_REASON.manual) => {
    if (!mediaRecorderRef.current) {
      // The permission prompt is still open (or the stream has not arrived yet), so there is nothing to
      // stop: remember why we were asked to, and let start() honour it once getUserMedia resolves.
      // Dropping it here would open the microphone right after the user cancelled.
      if (startingRef.current) pendingStopRef.current = reason;
      return;
    }

    stopReasonRef.current = reason;
    clearTimers();
    stopMetering();

    const recorder = mediaRecorderRef.current;
    if (recorder.state !== "inactive") recorder.stop(); // -> handleRecorderStop

    // Flip the UI immediately instead of waiting for onstop: the user has to perceive the exact moment
    // we stopped listening, especially when the silence detector stopped for them.
    setIsRecording(false);
    setStartedAt(null);
  };

  const tick = () => {
    const analyser = analyserRef.current;
    const buffer = bufferRef.current;
    if (!analyser || !buffer) return;

    analyser.getByteTimeDomainData(buffer);

    // RMS of the waveform around the 128 zero point gives a 0..1 loudness.
    let sum = 0;
    for (let i = 0; i < buffer.length; i += 1) {
      const sample = (buffer[i] - 128) / 128;
      sum += sample * sample;
    }
    const rms = Math.sqrt(sum / buffer.length);

    // Fast attack, slow release: the meter jumps with the voice and falls back smoothly.
    levelRef.current =
      rms > levelRef.current
        ? rms * 0.6 + levelRef.current * 0.4
        : rms * 0.15 + levelRef.current * 0.85;

    // Noise floor falls fast and rises very slowly, so it tracks the room (and any automatic gain
    // control drift) without being dragged upward by the speech itself.
    floorRef.current =
      rms < floorRef.current
        ? rms * 0.3 + floorRef.current * 0.7
        : rms * 0.005 + floorRef.current * 0.995;

    const now = performance.now();
    const threshold = Math.min(MAX_THRESHOLD, Math.max(MIN_THRESHOLD, floorRef.current * 3 + 0.01));

    if (levelRef.current > threshold) {
      hasSpeechRef.current = true;
      lastVoiceAtRef.current = now;
    }

    const elapsed = now - startedAtRef.current;
    // Grace period: never auto-stop while the user is still finding their words, and while the noise
    // floor estimate settles after the microphone opens.
    const quietFor = now - lastVoiceAtRef.current;

    if (elapsed > graceMs && hasSpeechRef.current && quietFor >= silenceTimeout) {
      stop(STOP_REASON.silence);
      return;
    }

    if (!hasSpeechRef.current && elapsed >= noSpeechTimeout) {
      stop(STOP_REASON.noSpeech);
      return;
    }

    rafRef.current = requestAnimationFrame(tick);
  };

  const start = async () => {
    if (isRecording || isStarting) return;

    const format = getSupportedFormat();

    // navigator.mediaDevices is undefined on an insecure origin, which for the user is the same dead
    // end as a browser without MediaRecorder.
    if (!format || !navigator.mediaDevices?.getUserMedia) {
      onErrorRef.current?.(RECORDER_ERROR.unsupported);
      return;
    }

    activeRecording?.stop(STOP_REASON.cancelled);
    pendingStopRef.current = null;
    startingRef.current = true;
    setIsStarting(true);

    // iOS Safari only lets an AudioContext start inside a user gesture, and awaiting getUserMedia below
    // breaks that chain — so it must be constructed before the first await and resumed after. A missing
    // or failing AudioContext is not fatal: we degrade to no meter and no silence auto-stop.
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    let audioContext = null;
    try {
      audioContext = AudioContextClass ? new AudioContextClass() : null;
    } catch {
      audioContext = null;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });

      // The component may have unmounted, or the user may have pressed cancel/send, while the
      // permission prompt was open. Either way the stream we just acquired has to go back before it
      // ever reaches a MediaRecorder, or the microphone stays open with no way to stop it.
      if (!mountedRef.current || pendingStopRef.current) {
        const pendingReason = pendingStopRef.current;
        pendingStopRef.current = null;
        startingRef.current = false;

        stream.getTracks().forEach(track => track.stop());
        audioContext?.close().catch(() => {});

        if (!mountedRef.current) return;

        setIsStarting(false);

        // Nothing was ever recorded, which for anything but a deliberate cancel is the same dead end as
        // an empty recording — reported the way handleRecorderStop reports it.
        if (pendingReason !== STOP_REASON.cancelled)
          onErrorRef.current?.(RECORDER_ERROR.empty, pendingReason);

        return;
      }

      streamRef.current = stream;
      formatRef.current = format;
      chunksRef.current = [];
      stopReasonRef.current = STOP_REASON.manual;
      hasSpeechRef.current = false;
      levelRef.current = 0;
      floorRef.current = 0;

      if (audioContext) {
        audioContextRef.current = audioContext;
        if (audioContext.state === "suspended") await audioContext.resume().catch(() => {});

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 1024; // about a 21 ms window at 48 kHz
        const sourceNode = audioContext.createMediaStreamSource(stream);
        sourceNode.connect(analyser); // deliberately not to the destination: no feedback loop
        analyserRef.current = analyser;
        sourceNodeRef.current = sourceNode;
        bufferRef.current = new Uint8Array(analyser.fftSize);
      }

      const recorder = new MediaRecorder(stream, {
        mimeType: format.mimeType,
        audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
      });
      recorder.ondataavailable = event => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };
      recorder.onstop = handleRecorderStop;
      recorder.onerror = () => stop(STOP_REASON.cancelled);
      recorder.start();

      mediaRecorderRef.current = recorder;
      activeRecording = { owner: instanceRef.current, stop };
      startingRef.current = false;
      startedAtRef.current = performance.now();
      lastVoiceAtRef.current = startedAtRef.current;

      // The hard cap lives on a timer rather than the animation frame loop: requestAnimationFrame is
      // paused in background tabs, so the loop (and with it the silence detection) can freeze while the
      // recorder keeps running.
      maxTimerRef.current = setTimeout(() => stop(STOP_REASON.maxDuration), maxDurationMs);

      setIsStarting(false);
      setIsRecording(true);
      setStartedAt(new Date());

      if (analyserRef.current) rafRef.current = requestAnimationFrame(tick);

      // A stop requested while the AudioContext was resuming above lands here: the recorder exists now,
      // so it can be stopped for real, after the state above so the flags it clears stick.
      if (pendingStopRef.current) {
        const pendingReason = pendingStopRef.current;
        pendingStopRef.current = null;
        stop(pendingReason);
      }
    } catch (error) {
      audioContext?.close().catch(() => {});
      releaseStream();
      startingRef.current = false;
      pendingStopRef.current = null;

      if (!mountedRef.current) return;

      setIsStarting(false);

      const name = error?.name;
      onErrorRef.current?.(
        name === "NotFoundError" ||
          name === "DevicesNotFoundError" ||
          name === "OverconstrainedError"
          ? RECORDER_ERROR.notFound
          : RECORDER_ERROR.permission
      );
    }
  };

  useEffect(() => {
    // Re-armed on every mount so React's development-mode double mount does not leave the hook
    // permanently believing it is unmounted.
    mountedRef.current = true;

    // Captured here rather than read in the cleanup: the token never changes, but reading a ref from a
    // cleanup function is exactly the pattern the hooks lint rule warns about.
    const instance = instanceRef.current;

    return () => {
      mountedRef.current = false;
      clearTimers();
      stopMetering();

      const recorder = mediaRecorderRef.current;
      if (recorder && recorder.state !== "inactive") {
        recorder.ondataavailable = null;
        recorder.onstop = null; // never touch state on an unmounted component
        recorder.stop();
      }
      mediaRecorderRef.current = null;
      if (activeRecording?.owner === instance) activeRecording = null;
      releaseStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isRecording, isStarting, startedAt, levelRef, start, stop };
}
